// Affiliate voucher types (SSD-5796), mirroring the internal-api COUPON contract shipped in SSD-5791.
// Money crosses the wire as a decimal string; it stays a string here and is parsed at the render boundary.

export type VoucherStatus = 'active' | 'disabled';

export type VoucherSource = 'generated' | 'imported';

export type VoucherSort = 'createdAt' | 'code' | 'orders' | 'revenue' | 'commission';

/** Personal codes belong to no campaign; campaign codes were issued for one affiliate campaign. */
export type VoucherCodeGroup = 'personal' | 'campaign';

/** Commission state of a single attributed order, as the attribution row records it. */
export type VoucherCommissionStatus = 'pending' | 'confirmed' | 'refunded' | 'cancelled';

export interface InfluencerVoucher {
  code: string;
  handle: string;
  platform: string;
  displayName?: string;
  /** Absent while the code is unclaimed: an imported code whose creator has not joined Creator Hub. */
  userId?: number;
  discountPercent: number;
  /** Every checkout redemption, whatever became of the order. */
  uses: number;
  /** Orders that completed, which is what commission is based on. */
  orderCount: number;
  revenueTotal: string;
  commissionTotal: string;
  /** Referral-link taps recorded by the click ledger (SSD-5811). */
  clicks: number;
  /** Completed orders attributed through the referral link, with or without the code. */
  utmOrders: number;
  /**
   * Per-code overrides of the tier rates, one per tracking source. Absent means the code
   * inherits the program default for that source (and later the campaign's rate, once
   * affiliate campaigns slot into the chain: code override > campaign > program).
   */
  commissionPercent?: number;
  utmPercent?: number;
  couponUtmPercent?: number;
  status: VoucherStatus;
  source: VoucherSource;
  /** The affiliate campaign the code was issued for; absent for a personal code. */
  campaignId?: string;
  /** Absent when the campaign can no longer be read. */
  campaignName?: string;
  createdAt: string;
}

export interface VoucherListTotals {
  activeCodes: number;
  disabledCodes: number;
  /** Lifetime across every code; WooCommerce keeps no per-period redemption counter. */
  redemptions: number;
  orderCount: number;
  revenueTotal: string;
  commissionTotal: string;
  commissionAccrued: string;
}

export interface VoucherListing {
  currency: string;
  totals: VoucherListTotals;
  codes: InfluencerVoucher[];
  total: number;
  limit: number;
  offset: number;
}

export interface VoucherListParams {
  /** Matches a code or a creator handle, case-insensitive. */
  search?: string;
  status?: VoucherStatus;
  codeGroup?: VoucherCodeGroup;
  /** Only the codes of this affiliate campaign. */
  campaignId?: string;
  from?: string;
  to?: string;
  sort?: VoucherSort;
  limit?: number;
  offset?: number;
}

/** The platforms a referral link can be tagged with (`/r/<code>?ch=instagram`); mirrors the internal-api enum. */
export enum ReferralChannel {
  Instagram = 'instagram',
  TikTok = 'tiktok',
  YouTube = 'youtube',
  Facebook = 'facebook',
  X = 'x',
  Threads = 'threads',
}

export const REFERRAL_CHANNEL_LABELS: Record<ReferralChannel, string> = {
  [ReferralChannel.Instagram]: 'Instagram',
  [ReferralChannel.TikTok]: 'TikTok',
  [ReferralChannel.YouTube]: 'YouTube',
  [ReferralChannel.Facebook]: 'Facebook',
  [ReferralChannel.X]: 'X',
  [ReferralChannel.Threads]: 'Threads',
};

export interface VoucherChannelClicks {
  channel: ReferralChannel;
  clicks: number;
}

export interface VoucherPerformanceWeek {
  weekStart: string;
  orderCount: number;
  revenue: string;
  commission: string;
}

export interface VoucherPerformanceOrder {
  orderId: number;
  date: string;
  revenue: string;
  commission: string;
  status: VoucherCommissionStatus;
  /** An admin payout row carries this order; this alone does not mean the creator was paid. */
  settled: boolean;
  /** Actual payout completion. Older API responses omit this field and must not display Paid. */
  paid?: boolean;
}

export interface VoucherPerformance {
  code: string;
  handle: string;
  platform: string;
  displayName?: string;
  discountPercent: number;
  /** Per-source rate overrides; absent means the code follows the program tier for that source. */
  commissionPercent?: number;
  utmPercent?: number;
  couponUtmPercent?: number;
  status: VoucherStatus;
  createdAt: string;
  currency: string;
  orderCount: number;
  revenueTotal: string;
  commissionTotal: string;
  /** Every referral-link tap in the window; the channel rows come zero-filled in enum order. */
  clicks: number;
  clicksByChannel: VoucherChannelClicks[];
  /** Taps whose link carried no channel tag. */
  untaggedClicks: number;
  weekly: VoucherPerformanceWeek[];
  orders: VoucherPerformanceOrder[];
}

export const MAX_VOUCHER_BATCH_SIZE = 500;

export interface GenerateVoucherTarget {
  userId: number;
  /** Omitted leaves the code on the program tiers; a value overrides the coupon tier for this code alone. */
  commissionPercent?: number;
}

export interface GenerateVouchersRequest {
  targets: GenerateVoucherTarget[];
  /** Batch-level audience discount; the backend falls back to the program rate when omitted. */
  discountPercent?: number;
}

export interface GenerateVouchersResult {
  generated: number;
  alreadyHadCode: number;
  skipped: number;
  errors: string[];
}

export interface ImportVoucherRow {
  handle: string;
  platform: string;
  /** Omitted leaves the code on the program tiers; a value overrides the coupon tier for this code alone. */
  commissionPercent?: number;
  /** Existing WooCommerce code to record as-is; omit to have the backend issue a new one. */
  couponCode?: string;
  discountPercent?: number;
}

export interface ImportVouchersRequest {
  rows: ImportVoucherRow[];
  importedBy: string;
  discountPercent?: number;
}

export interface ImportVouchersResult {
  imported: number;
  /** Recorded with no creator attached, waiting to be claimed by handle when that creator joins. */
  unclaimed: number;
  skipped: number;
  errors: string[];
}

export interface SetVoucherStatusRequest {
  code: string;
  status: VoucherStatus;
}

/** Full replacement of the code's overrides: an omitted rate clears that source's override so it inherits again. */
export interface SetVoucherCommissionRequest {
  code: string;
  commissionPercent?: number;
  utmPercent?: number;
  couponUtmPercent?: number;
}

/** What the two PATCH routes return: the stored code without its WooCommerce bookkeeping. */
export interface VoucherMutationResult {
  code: string;
  handle: string;
  platform: string;
  userId?: number;
  discountPercent: number;
  /** Per-source rate overrides; absent means the code follows the program tier for that source. */
  commissionPercent?: number;
  utmPercent?: number;
  couponUtmPercent?: number;
  status: VoucherStatus;
  source: VoucherSource;
  createdAt: string;
  updatedAt: string;
}

/** One set of the three tracking-source rates: coupon only / link only / link + coupon. */
export interface CommissionTierTrio {
  couponPercent: number;
  utmPercent: number;
  couponUtmPercent: number;
}

/**
 * Program-level tier defaults (SSD-5811). Codes without per-code overrides inherit these;
 * when affiliate campaigns arrive they carry their own trio between the two levels
 * (code override > campaign > program). `version` is the optimistic-concurrency token:
 * a save must echo the version it read, and a stale one is rejected with 409.
 */
export interface CommissionTierRates extends CommissionTierTrio {
  version: number;
}

export interface SetCommissionTierRatesRequest extends CommissionTierTrio {
  expectedVersion: number;
}

/** A creator offered in the generate picker, resolved from influencer search. */
export interface VoucherCandidate {
  influencerId: string;
  handle: string;
  platform: string;
  displayName: string;
  followersCount: number;
  existingCode: string | null;
}
